import React, { useMemo, useState } from 'react';
import { shape } from 'prop-types';
import Movies from '../Movies';
import Hero from '../Hero';
import Header from '../Header';
import Footer from '../Footer';
import PlayerModal from '../PlayerModal';
import DetailsModal from '../DetailsModal';
import {
  cloudinaryPreviewFromVideoUrl,
  getMuxPlaybackUrl,
  getMuxPosterUrl,
  resolveMediaUrl,
  resolvePosterUrl,
} from '../../lib/cloudinary';

function AlbumExperience({ data }) {
  const {
    categories = [],
    media = [],
    featured,
    profiles = [],
    ui = {},
    shared = {},
  } = data || {};

  const resolvedMedia = useMemo(() => media.map((item) => {
    const isMux = item.provider === 'mux' || item.muxPlaybackId;
    const imagePath = resolveMediaUrl({
      url: item.imagePath,
      publicId: item.imagePublicId,
      type: 'image',
    });
    const backgroundPath = resolveMediaUrl({
      url: item.backgroundPath,
      publicId: item.backgroundPublicId,
      type: 'image',
    }) || imagePath;
    const videoPath = isMux
      ? (item.videoPath || getMuxPlaybackUrl(item.muxPlaybackId))
      : resolveMediaUrl({
        url: item.videoPath,
        publicId: item.videoPublicId,
        type: 'video',
      });
    const videoPosterPath = resolvePosterUrl({
      url: item.videoPosterPath,
      publicId: item.videoPosterPublicId || item.videoPublicId,
    }) || imagePath || (item.muxPlaybackId ? getMuxPosterUrl(item.muxPlaybackId) : '');
    const muxMp4FallbackPath = isMux && item.muxPlaybackId
      ? `https://stream.mux.com/${item.muxPlaybackId}/low.mp4`
      : '';
    const previewVideoPath = isMux
      ? (item.previewVideoPath || item.videoPath || getMuxPlaybackUrl(item.muxPlaybackId))
      : (resolveMediaUrl({
        url: item.previewVideoPath || item.videoPreviewPath,
        publicId: item.videoPreviewPublicId,
        type: 'video',
      }) || cloudinaryPreviewFromVideoUrl(videoPath, { width: 640 }) || videoPath);

    return {
      ...item,
      isMux,
      imagePath,
      backgroundPath,
      videoPath,
      videoPosterPath,
      previewVideoPath,
      muxMp4FallbackPath,
      disableHoverPreview: isMux ? false : item.disableHoverPreview,
    };
  }), [media]);

  const featuredItems = (featured?.items || [])
    .map((id) => resolvedMedia.find((item) => item.id === id))
    .filter(Boolean);
  const featuredAlbum = categories.find((category) => category.id === featured?.albumId);
  const featuredAlbumItems = featuredAlbum
    ? featuredAlbum.items
      .map((id) => resolvedMedia.find((item) => item.id === id))
      .filter(Boolean)
    : [];

  const logoPath = resolveMediaUrl({
    url: ui.logoPath,
    publicId: ui.logoPublicId,
    type: 'image',
    width: 500,
  });
  const footerLogoPath = resolveMediaUrl({
    url: ui.footerLogoPath || shared.ui?.footerLogoPath || ui.logoPath,
    publicId: ui.footerLogoPublicId || ui.logoPublicId,
    type: 'image',
    width: 500,
  });
  const profilePath = resolveMediaUrl({
    url: ui.profilePath || profiles[0]?.avatarPath,
    publicId: ui.profilePublicId,
    type: 'image',
    width: 200,
  });
  const top10IconPath = resolveMediaUrl({
    url: ui.top10BadgePath,
    publicId: ui.top10BadgePublicId,
    type: 'image',
    width: 120,
  });
  const heroImageOverride = resolveMediaUrl({
    url: ui.heroImagePath,
    publicId: ui.heroImagePublicId,
    type: 'image',
    width: 1920,
  }) || featuredItems[0]?.backgroundPath || featuredItems[0]?.imagePath || shared.reusableMedia?.heroFallbackPath;

  const [modalItems, setModalItems] = useState([]);
  const [modalIndex, setModalIndex] = useState(0);
  const [detailsOpen, setDetailsOpen] = useState(false);

  return (
    <div>
      <Header logoPath={logoPath} profilePath={profilePath} />
      <Hero
        movies={featuredItems}
        mode={featured?.mode}
        heroImageOverride={heroImageOverride}
        heroImagePosition={ui.heroImagePosition}
        heroTitleOverride={ui.heroTitle}
        top10IconPath={top10IconPath}
        onPlay={() => {
          if (!featuredAlbumItems.length) return;
          setModalItems(featuredAlbumItems);
          setModalIndex(0);
        }}
        onMoreInfo={() => setDetailsOpen(true)}
      />
      {categories.map((category) => {
        const categoryItems = category.items
          .map((id) => resolvedMedia.find((item) => item.id === id))
          .filter(Boolean);
        return (
          <Movies
            key={category.id}
            title={category.title}
            movies={categoryItems}
            cardStyle={category.cardStyle}
            onSelect={(item) => {
              const index = categoryItems.findIndex((entry) => entry.id === item.id);
              setModalItems(categoryItems);
              setModalIndex(index >= 0 ? index : 0);
            }}
          />
        );
      })}
      <PlayerModal
        isOpen={modalItems.length > 0}
        items={modalItems}
        initialIndex={modalIndex}
        onClose={() => {
          setModalItems([]);
          setModalIndex(0);
        }}
      />
      <DetailsModal
        isOpen={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        album={featuredAlbum}
        hero={featuredItems[0]}
        heroImageOverride={heroImageOverride}
        items={featuredAlbumItems}
        cardStyle={featuredAlbum?.cardStyle}
        onPlayAlbum={() => {
          if (!featuredAlbumItems.length) return;
          setModalItems(featuredAlbumItems);
          setModalIndex(0);
        }}
        onSelectItem={(item) => {
          const index = featuredAlbumItems.findIndex((entry) => entry.id === item.id);
          setModalItems(featuredAlbumItems);
          setModalIndex(index >= 0 ? index : 0);
        }}
      />
      <Footer logoPath={footerLogoPath} />
    </div>
  );
}

AlbumExperience.propTypes = {
  data: shape,
}.isRequired;

export default AlbumExperience;
